import { useEffect, useState } from "react";
import { Target, Upload, X, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];
const fmt = (n) => new Intl.NumberFormat("es-DO").format(n || 0);

/**
 * Sales-Targets uploader — reads the annual `Plan (Unidades).xlsx` template
 * exported from PowerBI Analyzer and stores per-branch × brand × model ×
 * grade × month targets in the `sales_targets` collection.
 *
 * Two modes:
 *   - Replace (default): wipes the year and re-imports — safe for a full
 *     annual reload.
 *   - Append: upserts only what's in the file, keeps other rows.
 */
export default function TargetsUploadCard({ onUploaded }) {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [mode, setMode] = useState("replace");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);

  const loadStatus = () => {
    api.get(`/predictive/targets/status?year=${year}`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  };

  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, [year]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Selecciona el archivo .xlsx"); return; }
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("year", year);
      form.append("mode", mode);
      const r = await api.post("/predictive/targets/upload-xlsx", form,
                                { headers: { "Content-Type": "multipart/form-data" }});
      setResult(r.data);
      toast.success(`Metas ${year} cargadas — ${fmt(r.data.rows_atomic)} filas, ${fmt(r.data.rows_with_target)} con meta.`);
      loadStatus();
      onUploaded?.();
    } catch (err) {
      toast.error(formatApiError(err));
      setResult({ error: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setResult(null);
    const input = document.getElementById("targets-input");
    if (input) input.value = "";
  };

  return (
    <div className="surface p-5" data-testid="card-targets-upload">
      <div className="flex items-center gap-2 mb-1">
        <Target size={18} style={{ color: "#8b5cf6" }}/>
        <h3 className="font-display font-extrabold text-lg">Metas de Venta (Plan Anual)</h3>
      </div>
      <p className="text-xs text-gray-500 font-semibold mb-4">
        Sube el archivo <code className="bg-gray-100 px-1 rounded">Plan (Unidades).xlsx</code> exportado desde PowerBI Analyzer. Estructura: Unidad de Negocio · Marca · Modelo · Grado · Mes · Meta unidades.
      </p>

      {/* Current status */}
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <Stat label="Estado" value={
          status?.loaded
            ? <span style={{ color: "#059669" }}>✔ Cargado</span>
            : <span style={{ color: "#9ca3af" }}>—</span>
        }/>
        <Stat label={`Meta total ${year}`} value={status?.loaded ? `${fmt(status.total_units)} uds` : "—"}/>
        <Stat label="Última carga" value={
          status?.last_upload_at
            ? new Date(status.last_upload_at).toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "numeric" })
            : "—"
        }/>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">Año del plan</span>
            <select className="px-3 py-2 rounded-lg border border-gray-200 bg-white font-bold text-sm"
                    value={year} onChange={(e) => setYear(parseInt(e.target.value))}
                    data-testid="targets-year">
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">Modo</span>
            <select className="px-3 py-2 rounded-lg border border-gray-200 bg-white font-bold text-sm"
                    value={mode} onChange={(e) => setMode(e.target.value)}
                    data-testid="targets-mode">
              <option value="replace">Reemplazar año completo</option>
              <option value="append">Agregar / actualizar</option>
            </select>
          </label>
        </div>

        <label
          className="block rounded-xl border-2 border-dashed cursor-pointer p-4 text-center transition"
          style={{ borderColor: file ? "#8b5cf6" : "#d1d5db", background: file ? "#faf5ff" : "#fafbfc" }}
          data-testid="targets-dropzone"
        >
          <input id="targets-input" type="file" accept=".xlsx,.xls" className="hidden"
                 onChange={(e) => setFile(e.target.files?.[0] || null)}/>
          {file ? (
            <div className="flex items-center justify-center gap-2">
              <FileSpreadsheet size={18} style={{ color: "#8b5cf6" }}/>
              <span className="font-bold text-sm">{file.name}</span>
              <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(0)} KB)</span>
              {!busy && (
                <button type="button" onClick={(e) => { e.preventDefault(); clearFile(); }}
                        className="rounded-full p-1 hover:bg-red-100 transition"
                        style={{ color: "#dc2626", lineHeight: 0 }}
                        title="Quitar archivo"
                        data-testid="targets-remove-file">
                  <X size={14}/>
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-sm font-semibold text-gray-600">
              <Upload size={16}/> Click para seleccionar Plan (Unidades).xlsx
            </div>
          )}
        </label>

        <button type="submit" disabled={!file || busy}
                className="px-4 py-2 rounded-lg font-extrabold text-sm flex items-center gap-2 disabled:opacity-40"
                style={{ background: "#8b5cf6", color: "#fff" }}
                data-testid="targets-submit">
          {busy ? <><Loader2 size={14} className="animate-spin"/> Procesando…</>
                : <><Upload size={14}/> Subir metas {year}</>}
        </button>
      </form>

      {result?.ok && (
        <div className="mt-4 p-3 rounded-lg flex items-start gap-2"
             style={{ background: "#f0fdf4", border: "1px solid #86efac" }}
             data-testid="targets-result-ok">
          <CheckCircle2 size={16} style={{ color: "#059669", flexShrink: 0, marginTop: 2 }}/>
          <div className="text-xs font-semibold text-green-900">
            Metas <strong>{result.year}</strong> cargadas correctamente en modo <strong>{result.mode}</strong>.
            <div className="mt-1 text-green-800">
              {fmt(result.rows_read)} filas leídas · {fmt(result.rows_atomic)} atómicas guardadas · {fmt(result.rows_with_target)} con meta &gt; 0.
            </div>
          </div>
        </div>
      )}
      {result?.error && (
        <div className="mt-4 p-3 rounded-lg flex items-start gap-2"
             style={{ background: "#fef2f2", border: "1px solid #fecaca" }}
             data-testid="targets-result-error">
          <AlertCircle size={16} style={{ color: "#dc2626", flexShrink: 0, marginTop: 2 }}/>
          <div className="text-xs font-semibold text-red-900">{result.error}</div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="surface p-3" style={{ background: "#fafbfc" }}>
      <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      <div className="font-display font-extrabold text-lg">{value}</div>
    </div>
  );
}
